import React, { useContext, useMemo, useState } from "react";

import { useHistory } from "react-router-dom";

import Box from "@/components/Box";
import DataLoader from "@/components/DataLoader";
import Text, { SpanText } from "@/components/Text";
import {
  enrichRowWithReportInfo,
  isReportOrderTestItem
} from "@/components/reports/reportListClick";
import {
  statusFromOrderTestItem,
  RESULTS_AVAILABLE,
  CONSULT_NEEDED,
  statusFromUserTest
} from "@/core/utils";
import { ORDER_TEST_ITEM_FIELDS } from "@/graphql/shop";
import { USER_TEST_FIELDS_TRUNCATED } from "@/graphql/tpo/results/types";
import { useDidUpdate } from "@/hooks/useDidUpdate";
import TestIcon from "@/images/TestOrderIcon";
import { gql } from "@apollo/client";
import PropTypes from "prop-types";

import { SelectDropdownStatusIcon, SelectExpand } from "./Expand";
import Group from "./Group";
import { ViewerContext } from "./Viewer";
import { PROGRESS_STATUS_FAILED } from "./shop/constants";

export const USER_TESTS_AND_ORDERS_QUERY = gql`
  query UserTestsAndOrdersQuery($allTests: Boolean, $excludeRetests: Boolean) {
    userTests(allTests: $allTests, excludeRetests: $excludeRetests) {
      id
      name
      slug
      createdDateFormatted
      reviewStatus
      orderTestItem {
        id
        order {
          id
        }
      }
    }
    ordersInProgress {
      id
      resultsReceivedByProvider
      sampleReceivedByProvider
      sampleReceivedByLab
      nameInBasket
      registered
      testSentToUser
      product {
        id
        name
        paymentIsoCode
        organisation
      }
      userTestIds
      order {
        id
      }
    }
  }
`;

export const CLIENT_TESTS = gql`
  query ClientTestsQuery($userId: ID) {
    userTests(userId: $userId) {
      ...UserTestFieldsTruncated
      orderTestItem {
        id
        order {
          id
        }
      }
    }
    ordersInProgress(userId: $userId) {
      ...OrderTestItemFields
      order {
        id
      }
    }
  }
  ${ORDER_TEST_ITEM_FIELDS}
  ${USER_TEST_FIELDS_TRUNCATED}
`;

export function getUserTestOrderId(userTest) {
  if (userTest?.externalSourceTest) {
    return null;
  }

  return userTest?.displayOrderId ?? userTest?.orderTestItem?.order?.id ?? null;
}

export function getOrderTestItemOrderId(orderTestItem) {
  return orderTestItem?.displayOrderId ?? orderTestItem?.order?.id ?? null;
}

export function getUserTestPaymentStatus(userTest) {
  if (userTest?.externalSourceTest) {
    return null;
  }

  return userTest?.displayPaymentStatus ?? userTest?.orderTestItem?.order?.derivedPaymentStatus ?? null;
}

function combineOrdersAndResults({ testResults, ordersInProgress, terminology }) {
  let combinedStatuses = [];
  let seenTestIds = new Set();

  testResults.forEach(userTest => {
    seenTestIds.add(userTest.id);

    const { status, isLink, variant } = statusFromUserTest(userTest, terminology);

    combinedStatuses.push({
      name: userTest.name,
      slug: `${userTest.slug}/${userTest.id}`,
      id: userTest.id,
      created: userTest.createdDateFormatted,
      status,
      isUserTest: true,
      orderId: getUserTestOrderId(userTest),
      userTest,
      isLink,
      variant
    });
  });

  ordersInProgress.forEach(orderTestItem => {
    // if this order has any tests that are in the test results, hide it
    if (orderTestItem.userTestIds?.some(userTestId => seenTestIds.has(userTestId))) {
      return;
    }

    const { status, variant } = statusFromOrderTestItem(orderTestItem);

    combinedStatuses.push({
      name: orderTestItem.nameInBasket,
      id: orderTestItem.id,
      orderId: getOrderTestItemOrderId(orderTestItem),
      orderTestItem,
      isReport: isReportOrderTestItem(orderTestItem),
      variant,
      status
    });
  });

  return combinedStatuses;
}

export function useTestStatuses({ testResults, ordersInProgress }) {
  const ctx = useContext(ViewerContext);
  const practitionerIsViewing = !!ctx;

  const terminology = useMemo(() => {
    return practitionerIsViewing
      ? {
          "Under Review": "For Review"
        }
      : {};
  }, [practitionerIsViewing]);

  const [statuses, setStatuses] = useState(
    combineOrdersAndResults({
      testResults,
      ordersInProgress,
      terminology
    })
  );

  useDidUpdate(() => {
    setStatuses(
      combineOrdersAndResults({
        testResults,
        ordersInProgress,
        terminology
      })
    );
  }, [testResults, ordersInProgress]);

  return statuses;
}

export default function TestStatuses({
  ordersInProgress = [],
  testResults = [],
  handleSelect,
  heading,
  defaultOpen,
  selectProps,
  reports = [],
  userReports = [],
  userEntitlements = [],
  reportViewType,
  getTestResultUrl,
  getReportUrl
}) {
  const history = useHistory();
  const statuses = useTestStatuses({
    testResults,
    ordersInProgress
  });

  const enrichedStatuses = useMemo(
    () =>
      statuses.map(status =>
        enrichRowWithReportInfo(status, {
          reports,
          userReports,
          userEntitlements,
          reportViewType
        })
      ),
    [statuses, reports, userReports, userEntitlements, reportViewType]
  );

  return (
    <SelectExpand
      {...selectProps}
      push={false}
      defaultOpen={defaultOpen}
      SelectDropdownIcon={SelectDropdownStatusIcon}
      top={
        <Box display="flex" alignItems="center">
          <TestIcon color="white" width={20} />
          <Text color="white" fontFamily="gilroyBold" ml="10px">
            {heading}
          </Text>
        </Box>
      }
      stackedBottomPx={0}
      withDivider
      itemsGap="20px"
      callback={testStatus => {
        if (handleSelect) {
          return handleSelect(testStatus);
        }
        if (testStatus.isUserTest && testStatus.isLink && getTestResultUrl) {
          return history.push(getTestResultUrl(testStatus.id));
        }
        if (testStatus.isReport && testStatus.canClickReport && testStatus.report && getReportUrl) {
          return history.push(getReportUrl(testStatus.report));
        }
      }}
      items={enrichedStatuses.map(testStatus => {
        const resultsAvailable =
          testStatus.status === RESULTS_AVAILABLE || testStatus.status === CONSULT_NEEDED;
        const statusText = testStatus.isReport
          ? testStatus.orderTestItem?.hasUserReport
            ? "Available"
            : "Awaiting Results"
          : testStatus.status;
        const isClickable = testStatus.isReport
          ? testStatus.canClickReport
          : resultsAvailable;
        return {
          value: testStatus,
          content: (
            <Box
              display="flex"
              px={20}
              justifyContent="space-between"
              textAlign="left"
              fontSize={14}
              flexWrap="wrap"
            >
              <Group flex={1}>
                <Box>
                  <Box>
                    <SpanText lineHeight={1} color="white">
                      {testStatus.name}
                    </SpanText>
                  </Box>
                  {(!!testStatus.orderId ||
                    testStatus.userTest?.externalSourceTest ||
                    testStatus.created) && (
                    <Group mt={1}>
                      {testStatus.userTest?.externalSourceTest ? (
                        <SpanText lineHeight={1} color="#818181">
                          External source
                        </SpanText>
                      ) : (
                        !!testStatus.orderId && (
                          <SpanText lineHeight={1} color="#818181">
                            {testStatus.orderId}
                          </SpanText>
                        )
                      )}
                      {(!!testStatus.orderId || testStatus.userTest?.externalSourceTest) &&
                        testStatus.created && (
                          <SpanText lineHeight={1} color="#818181" mx={1}>
                            |
                          </SpanText>
                        )}
                      {testStatus.created && (
                        <SpanText lineHeight={1} color="#818181">
                          {testStatus.created}
                        </SpanText>
                      )}
                    </Group>
                  )}
                </Box>
              </Group>
              <Box textAlign="right">
                <SpanText
                  lineHeight={1}
                  color={isClickable ? "green" : "white"}
                  underline={isClickable}
                >
                  {statusText}
                </SpanText>
              </Box>
            </Box>
          )
        };
      })}
    />
  );
}

TestStatuses.defaultProps = {
  heading: "Your tests",
  ordersInProgress: [],
  testResults: [],
  defaultOpen: true,
  selectProps: {
    maxWidth: 500,
    maxHeight: 500,
    px: 20,
    py: 25
  }
};

TestStatuses.propTypes = {
  testResults: PropTypes.arrayOf(PropTypes.object).isRequired,
  handleSelect: PropTypes.func,
  defaultOpen: PropTypes.bool,
  heading: PropTypes.string
};

// postpone props till later because ths isn't used atm
// or delete if we don't need it
export function TestStatusesLoader({ handleSelect, selectProps = {}, isPartnerView, clientId }) {
  return (
    <Box dataComponentName="Test statuses loader">
      <DataLoader
        query={isPartnerView ? CLIENT_TESTS : USER_TESTS_AND_ORDERS_QUERY}
        variables={isPartnerView ? { userId: clientId } : { allTests: true, excludeRetests: true }}
        loading={<TestStatuses heading="Loading" selectProps={selectProps} />}
        // Don't rely on the cache as the Stripe webhook is likely to mutate orders post checkout, rendering cached order statuses stale
        fetchPolicy="network-only"
        render={({ ordersInProgress = [], userTests }) => {
          if (ordersInProgress.length > 0 || userTests.length > 0) {
            return (
              <TestStatuses
                heading={isPartnerView ? "Available tests" : "Your tests"}
                ordersInProgress={ordersInProgress}
                testResults={userTests}
                handleSelect={handleSelect}
                selectProps={selectProps}
              />
            );
          } else {
            return <TestStatuses heading="No orders" selectProps={selectProps} />;
          }
        }}
      ></DataLoader>
    </Box>
  );
}
